import { useEffect, useState } from "react";

/**
 * Poll `fn` every `interval` ms — without the error loops raw `setInterval`
 * gives (M4.1):
 *
 *   - a `setTimeout` chain, so a slow call never overlaps the next one;
 *   - each failure doubles the wait (up to `maxInterval`), a success resets it;
 *   - 401 / 403 / 404 (`error.response.status`) stop it for good — re-asking
 *     cannot change a settled "no" — until `enabled` flips, `fn` changes or the
 *     component remounts;
 *   - a hidden tab schedules nothing; becoming visible runs one call at once
 *     and the chain carries on from there;
 *   - unmount clears everything, and no state is set after it.
 *
 * `fn` must REJECT on failure for any of this to work. The global interceptor
 * resolves a 5xx as `undefined` (src/core/errors/interceptorPolicy.js), so
 * wrap such calls in `requireResponse()`, or send them with
 * `metadata: { quiet: true }` / `{ silent: true }` so they reject.
 *
 * `fn` is called with `{ failing }` — true while the previous call failed — so
 * a caller can pass `metadata: { quiet: failing }`: the first failure of an
 * outage is announced as usual, the repeats are not.
 *
 * `fn` is an effect dependency: pass a stable (useCallback) function. A new
 * `fn` restarts the chain, which is how a poll follows a changed id.
 *
 * Returns `{ failing, stopped }` for the screen's own inline state.
 */
export const STOP_STATUSES = new Set([401, 403, 404]);
export const MAX_POLL_INTERVAL = 5 * 60_000;

export function usePoll(fn, { interval, enabled = true, maxInterval = MAX_POLL_INTERVAL, immediate = false } = {}) {
  const [failing, setFailing] = useState(false);
  const [stopped, setStopped] = useState(false);

  useEffect(() => {
    setFailing(false);
    setStopped(false);
    if (!enabled || !interval) return undefined;

    let cancelled = false;
    let done = false;
    let inFlight = false;
    let timer = null;
    let delay = interval;
    let lastFailed = false;

    const schedule = (ms) => {
      if (cancelled || done || document.hidden) return;
      timer = setTimeout(run, ms);
    };

    async function run() {
      timer = null;
      if (cancelled || done || inFlight || document.hidden) return;
      inFlight = true;
      try {
        await fn({ failing: lastFailed });
        if (cancelled) return;
        lastFailed = false;
        delay = interval;
        setFailing(false);
      } catch (error) {
        if (cancelled) return;
        lastFailed = true;
        if (STOP_STATUSES.has(error?.response?.status)) {
          done = true;
          setStopped(true);
          setFailing(false);
          return;
        }
        delay = Math.min(delay * 2, maxInterval);
        setFailing(true);
      } finally {
        inFlight = false;
      }
      schedule(delay);
    }

    const onVisibility = () => {
      if (document.hidden || timer || inFlight) return;
      run();
    };

    document.addEventListener("visibilitychange", onVisibility);
    if (immediate) run();
    else schedule(interval);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fn, enabled, interval, maxInterval, immediate]);

  return { failing, stopped };
}
